import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { useStore } from '../store/useStore';
import {
  getFormTemplates,
  getFormSubmissions,
  createFormSubmission,
  deleteFormSubmission,
  getTemplatePermissions,
  setTemplatePermissions,
} from '../utils/api';
import DailyReportForm from '../components/DailyReportForm';
import PdfFormFiller from '../components/PdfFormFiller';
import TemplatePermissionsModal from '../components/TemplatePermissionsModal';
import TemplateManagerModal from '../components/TemplateManagerModal';
import styles from './ProjectForms.module.css';

// Top-level Forms page. Three views in one component, switched by 'mode':
//   - 'list'   : table of submissions (filtered by template + status)
//   - 'fill'   : DailyReportForm in edit mode (new draft or editing existing)
//   - 'detail' : DailyReportForm in read-only mode (PM viewing a submitted form)

const STATUS_FILTERS = [
  { value: 'all',       label: 'All' },
  { value: 'draft',     label: 'Drafts' },
  { value: 'submitted', label: 'Submitted' },
];

export default function ProjectForms() {
  const { project, currentUser, role } = useStore();
  const isWriter = role === 'admin' || role === 'pm';

  const [templates, setTemplates]   = useState([]);
  const [activeTemplateId, setActiveTemplateId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [submissions, setSubmissions] = useState([]);
  const [loadingSubs, setLoadingSubs] = useState(false);

  // mode: { name: 'list' } | { name: 'fill', submissionId } | { name: 'detail', submissionId }
  const [mode, setMode] = useState({ name: 'list' });

  const [permModalOpen, setPermModalOpen]     = useState(false);
  const [tmplModalOpen, setTmplModalOpen]     = useState(false);
  const [allowed, setAllowed] = useState(false); // can current user fill the active template?

  // ── Load templates for project ──
  async function refreshTemplates() {
    if (!project) return;
    try {
      const list = await getFormTemplates(project.id);
      setTemplates(list);
      setActiveTemplateId((cur) => cur || (list[0]?.id ?? null));
    } catch (err) { toast.error(err.message); }
  }
  useEffect(() => { refreshTemplates(); /* eslint-disable-next-line */ }, [project?.id]);

  // ── Compute "allowed to fill" for the active template ──
  // admin/pm always; otherwise hit /permissions and look for a matching row.
  useEffect(() => {
    if (!activeTemplateId || !currentUser) { setAllowed(false); return; }
    if (isWriter) { setAllowed(true); return; }
    let alive = true;
    getTemplatePermissions(activeTemplateId).then((perms) => {
      if (!alive) return;
      const ok = perms.some((p) => p.user_id === currentUser.id || p.role === currentUser.role);
      setAllowed(ok);
    }).catch(() => alive && setAllowed(false));
    return () => { alive = false; };
  }, [activeTemplateId, currentUser, isWriter]);

  // ── Load submissions ──
  async function refreshSubmissions() {
    if (!project || !activeTemplateId) return;
    setLoadingSubs(true);
    try {
      const params = { project_id: project.id, template_id: activeTemplateId };
      if (statusFilter !== 'all') params.status = statusFilter;
      const list = await getFormSubmissions(params);
      setSubmissions(list);
    } catch (err) { toast.error(err.message); }
    finally { setLoadingSubs(false); }
  }
  useEffect(() => {
    if (mode.name === 'list') refreshSubmissions();
    /* eslint-disable-next-line */
  }, [project?.id, activeTemplateId, statusFilter, mode.name]);

  const activeTemplate = useMemo(
    () => templates.find((t) => t.id === activeTemplateId) || null,
    [templates, activeTemplateId]
  );

  async function handleNewSubmission() {
    if (!activeTemplate || !allowed) return;
    try {
      const sub = await createFormSubmission({
        template_id: activeTemplate.id,
        project_id: project.id,
        data: defaultDataForTemplate(activeTemplate),
        status: 'draft',
      });
      setMode({ name: 'fill', submissionId: sub.id });
    } catch (err) { toast.error(err.message); }
  }

  async function handleDelete(sub) {
    if (!window.confirm('Delete this submission? This cannot be undone.')) return;
    try {
      await deleteFormSubmission(sub.id);
      toast.success('Deleted');
      refreshSubmissions();
    } catch (err) { toast.error(err.message); }
  }

  async function handleSavePermissions(payload) {
    try {
      await setTemplatePermissions(activeTemplate.id, payload);
      toast.success('Permissions updated');
      setPermModalOpen(false);
    } catch (err) { toast.error(err.message); }
  }

  // ── Render ──
  if (!project) return null;

  if (mode.name === 'fill' || mode.name === 'detail') {
    // Pick the editor component based on the active template's kind.
    // 'daily_report' → structured editor; 'pdf' → AcroForm overlay filler.
    const Editor = activeTemplate?.kind === 'pdf' ? PdfFormFiller : DailyReportForm;
    return (
      <Editor
        submissionId={mode.submissionId}
        readOnly={mode.name === 'detail'}
        onClose={() => setMode({ name: 'list' })}
      />
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.tabs}>
          {templates.map((t) => (
            <button
              key={t.id}
              className={`${styles.tab} ${activeTemplateId === t.id ? styles.tabActive : ''}`}
              onClick={() => setActiveTemplateId(t.id)}
            >
              {t.name}
            </button>
          ))}
        </div>

        <div className={styles.actions}>
          <div className={styles.filterGroup}>
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.value}
                className={`${styles.filterBtn} ${statusFilter === f.value ? styles.filterActive : ''}`}
                onClick={() => setStatusFilter(f.value)}
              >
                {f.label}
              </button>
            ))}
          </div>

          {isWriter && (
            <button
              className={styles.btnSecondary}
              title="Add or remove form templates"
              onClick={() => setTmplModalOpen(true)}
            >
              <PlusIcon />
              Templates
            </button>
          )}

          {isWriter && activeTemplate && (
            <button
              className={styles.btnSecondary}
              title="Permissions"
              onClick={() => setPermModalOpen(true)}
            >
              <GearIcon />
              Permissions
            </button>
          )}

          {allowed && activeTemplate && (
            <button className={styles.btnPrimary} onClick={handleNewSubmission}>
              <PlusIcon />
              New {activeTemplate.name}
            </button>
          )}
        </div>
      </header>

      {activeTemplate?.description && (
        <div className={styles.description}>{activeTemplate.description}</div>
      )}

      <div className={styles.tableWrap}>
        {loadingSubs && <div className={styles.empty}>Loading…</div>}

        {!loadingSubs && submissions.length === 0 && (
          <div className={styles.empty}>
            {statusFilter === 'all'
              ? (allowed
                  ? `No submissions yet. Click "New ${activeTemplate?.name}" to start one.`
                  : 'No submissions yet.')
              : `No ${statusFilter} submissions.`}
          </div>
        )}

        {submissions.length > 0 && (
          <div className={styles.table}>
            <div className={styles.tableHeader}>
              <span>Date</span>
              <span>Status</span>
              <span>Submitted by</span>
              <span>Last update</span>
              <span></span>
            </div>
            {submissions.map((s) => (
              <SubmissionRow
                key={s.id}
                sub={s}
                isWriter={isWriter}
                currentUserId={currentUser?.id}
                onOpen={(opts) => setMode({ name: opts.edit ? 'fill' : 'detail', submissionId: s.id })}
                onDelete={() => handleDelete(s)}
              />
            ))}
          </div>
        )}
      </div>

      {permModalOpen && activeTemplate && (
        <TemplatePermissionsModal
          template={activeTemplate}
          onClose={() => setPermModalOpen(false)}
          onSave={handleSavePermissions}
        />
      )}

      {tmplModalOpen && (
        <TemplateManagerModal
          onClose={() => setTmplModalOpen(false)}
          onChanged={refreshTemplates}
        />
      )}
    </div>
  );
}

// Default JSON shape per template kind. Keeps a brand-new draft from
// blowing up when fields try to render before the user touches them.
function defaultDataForTemplate(t) {
  if (t.kind === 'daily_report') {
    return {
      form_date: new Date().toISOString().slice(0, 10),
      description_of_work: '',
      concrete_log: [],
      extra_work: 'na',
      extra_work_note: '',
    };
  }
  if (t.kind === 'pdf') {
    // PDF templates store a flat fieldName→value map under .fields. Server
    // tolerates missing keys, so an empty object is fine for a fresh draft.
    return { fields: {} };
  }
  return {};
}

// ── SubmissionRow ──
function SubmissionRow({ sub, isWriter, currentUserId, onOpen, onDelete }) {
  const date = sub.data?.form_date
    ? format(new Date(sub.data.form_date + 'T00:00:00'), 'MMM d, yyyy')
    : (sub.created_at ? format(new Date(sub.created_at), 'MMM d, yyyy') : '—');
  const lastUpdate = sub.updated_at ? format(new Date(sub.updated_at), 'MMM d, yyyy h:mm a') : '—';
  const isMine = sub.created_by_id === currentUserId;
  const canEdit   = isWriter || (isMine && sub.status === 'draft');
  const canDelete = isWriter || (isMine && sub.status === 'draft');

  return (
    <div className={styles.tableRow}>
      <button className={styles.rowMain} onClick={() => onOpen({ edit: false })}>
        <span className={styles.cellDate}>{date}</span>
        <span className={`${styles.statusPill} ${sub.status === 'submitted' ? styles.statusSubmitted : styles.statusDraft}`}>
          {sub.status.toUpperCase()}
        </span>
        <span className={styles.cellPerson}>{sub.submitted_by || sub.created_by || '—'}</span>
        <span className={styles.cellMeta}>{lastUpdate}</span>
      </button>
      <div className={styles.rowActions}>
        {canEdit && (
          <button className={styles.iconBtn} title="Edit" onClick={() => onOpen({ edit: true })}>
            <PencilIcon />
          </button>
        )}
        {canDelete && (
          <button className={styles.iconBtn} title="Delete" onClick={onDelete}>
            <TrashIcon />
          </button>
        )}
      </div>
    </div>
  );
}

// ── Icons ──
const GearIcon = () => (
  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
    <circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.4"/>
    <path d="M10 2v2M10 16v2M2 10h2M16 10h2M4.22 4.22l1.42 1.42M14.36 14.36l1.42 1.42M4.22 15.78l1.42-1.42M14.36 5.64l1.42-1.42"
      stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
  </svg>
);
const PlusIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M6 2v8M2 6h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
  </svg>
);
const PencilIcon = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
    <path d="M11 2l3 3-8 8H3v-3l8-8z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
  </svg>
);
const TrashIcon = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
    <path d="M3 4h10M6 4V3h4v1M5 4l.5 9h5L11 4"
      stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
